import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";
import { useNavigate, useOutletContext } from "react-router";
import { Button } from "@/components/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Icon } from "@/components/icons";
import { IntroHeader } from "@/components/intro-header";
import { useToast } from "@/components/action-feedback";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import type { Batch } from "../../_data/batches";
import {
  clearBatches,
  getBatch,
  getLatestBatchId,
  listBatches,
  removeBatch,
  uploadBatch,
} from "@/src/services/batch_service";
import { captureEvent } from "@/src/lib/posthog";
import { validateBatchFile } from "../_schemas/batch-upload";
import { BatchHistoryList } from "./batch-history-list";

type LoadStatus = "loading" | "error" | "ready";

export function BatchEmptyState() {
  const navigate = useNavigate();
  const ctx = useOutletContext<AppOutletContext | null>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canUpload = !(ctx?.infoResolved ?? false) || ctx?.apiUser?.accountType !== "auditor";

  const [status, setStatus] = useState<LoadStatus>("loading");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingDeleteBatch, setPendingDeleteBatch] = useState<Batch | null>(null);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const showToast = useToast();

  const load = useCallback(async () => {
    setStatus("loading");
    const res = await listBatches();
    if (res.ok) {
      setBatches(res.value);
      setStatus("ready");
    } else {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function processFile(file: File) {
    const validationError = validateBatchFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploadError(null);
    setUploading(true);
    const res = await uploadBatch({ file });
    if (!res.ok) {
      setUploading(false);
      setUploadError(res.error.message || "Couldn't upload the file. Please try again.");
      return;
    }
    captureEvent("Upload batch");
    showToast(
      res.value.parsingErrorMessages?.length
        ? {
            tone: "error",
            text: "This batch was uploaded with parsing errors. Check the batch list for details.",
          }
        : { tone: "success", text: "Batch query uploaded successfully." },
    );
    const listRes = await getLatestBatchId();
    setUploading(false);
    const id = listRes.ok ? listRes.value : res.value.id;
    navigate(`/batch/${id}`);
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void processFile(file);
  }

  async function handleRemove(id: string) {
    setActionError(null);
    const prev = batches;
    setBatches((cur) => cur.filter((b) => b.id !== id));
    const res = await removeBatch(id);
    if (!res.ok) {
      setBatches(prev);
      setActionError(res.error.message || "Couldn't remove the batch. Please try again.");
      return;
    }
    showToast({ tone: "success", text: "The batch has been deleted." });
  }

  async function handleDownload(id: string) {
    setActionError(null);
    const res = await getBatch(id);
    if (res.ok && res.value.downloadUrl) {
      window.open(res.value.downloadUrl, "_blank", "noopener,noreferrer");
    } else {
      setActionError("Couldn't get the download link. Please try again.");
    }
  }

  async function handleClearAll() {
    setActionError(null);
    const prev = batches;
    setBatches([]);
    const res = await clearBatches();
    if (!res.ok) {
      setBatches(prev);
      setActionError(res.error.message || "Couldn't clear the history. Please try again.");
      return;
    }
    showToast({ tone: "success", text: "Batch history cleared successfully." });
  }

  return (
    <div className="flex flex-1 flex-col items-center bg-white px-4 pb-4 pt-8 sm:px-8 lg:pt-[20vh]">
      <div className="flex w-full max-w-[871px] flex-col gap-6">
        <div className="flex flex-col gap-2">
          <IntroHeader
            iconName="upload_file"
            title="Batch screen names"
            description="Upload a list of names or organizations and screen them in one run against sanctions, PEP, and adverse media data."
          />
          {canUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleFile}
              />
              <UploadCard
                onUpload={() => fileInputRef.current?.click()}
                onFileDrop={(file) => void processFile(file)}
                onDownloadTemplate={downloadBatchTemplate}
              />
              {uploading && (
                <p role="status" className="text-small text-grey-900">
                  Uploading…
                </p>
              )}
              {uploadError && (
                <p role="alert" className="flex items-center gap-1.5 text-small text-red-600">
                  <Icon name="error" size={20} className="shrink-0" />
                  {uploadError}
                </p>
              )}
            </>
          )}
        </div>

        {actionError && (
          <p role="alert" className="flex items-center gap-1.5 text-small text-red-600">
            <Icon name="error" size={20} className="shrink-0" />
            {actionError}
          </p>
        )}
        <RecentBatches
          status={status}
          batches={batches}
          onRetry={load}
          onRemove={(id) => setPendingDeleteBatch(batches.find((b) => b.id === id) ?? null)}
          onDownload={handleDownload}
          onClearAll={() => setClearAllConfirmOpen(true)}
          canUpload={canUpload}
        />
      </div>

      <ConfirmDialog
        open={pendingDeleteBatch !== null}
        onClose={() => setPendingDeleteBatch(null)}
        onConfirm={() => {
          if (pendingDeleteBatch) void handleRemove(pendingDeleteBatch.id);
          setPendingDeleteBatch(null);
        }}
        title="Delete this batch?"
        description={
          pendingDeleteBatch
            ? `This will permanently delete "${pendingDeleteBatch.filename}" and its results. This can't be undone.`
            : undefined
        }
        confirmLabel="Delete batch"
        destructive
        insetLeft={ctx?.mainInsetLeft}
      />

      <ConfirmDialog
        open={clearAllConfirmOpen}
        onClose={() => setClearAllConfirmOpen(false)}
        onConfirm={() => {
          void handleClearAll();
          setClearAllConfirmOpen(false);
        }}
        title="Clear all batch history?"
        description="This will permanently delete all your batch screening history and results. This can't be undone."
        confirmLabel="Clear all"
        destructive
        insetLeft={ctx?.mainInsetLeft}
      />
    </div>
  );
}

export const BATCH_TEMPLATE_URL =
  "https://sanctions-static.s3.eu-west-1.amazonaws.com/templates/batch-screening/batch_screening__template.xlsx";

export function downloadBatchTemplate() {
  const a = document.createElement("a");
  a.href = BATCH_TEMPLATE_URL;
  a.download = "batch_screening__template.xlsx";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function RecentBatches({
  status,
  batches,
  onRetry,
  onRemove,
  onDownload,
  onClearAll,
  canUpload,
}: {
  status: LoadStatus;
  batches: Batch[];
  onRetry: () => void;
  onRemove: (id: string) => void;
  onDownload: (id: string) => void;
  onClearAll: () => void;
  canUpload: boolean;
}) {
  if (status === "loading") return <RecentBatchesSkeleton />;
  if (status === "error") return <RecentBatchesError onRetry={onRetry} />;
  if (batches.length === 0) return <RecentBatchesEmpty canUpload={canUpload} />;
  return (
    <BatchHistoryList
      batches={batches}
      onRemove={onRemove}
      onDownload={onDownload}
      onClearAll={onClearAll}
    />
  );
}

function RecentBatchesSkeleton() {
  return (
    <section className="flex w-full flex-col gap-2" aria-busy="true">
      <h3 className="text-body font-medium text-black">Recent batches</h3>
      <div className="flex flex-col overflow-hidden rounded-sm border border-grey-200 bg-white shadow-flat">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={[
              "flex items-center gap-4 px-4 py-2",
              i < 2 ? "border-b border-grey-100" : "",
            ].join(" ")}
          >
            <div className="h-9 w-7.5 shrink-0 animate-pulse rounded-sm bg-grey-100" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="h-3.5 w-2/5 animate-pulse rounded bg-grey-100" />
              <div className="h-3 w-3/5 animate-pulse rounded bg-grey-100" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecentBatchesError({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="flex w-full flex-col gap-2">
      <h3 className="text-body font-medium text-black">Recent batches</h3>
      <div className="flex flex-col items-center gap-3 rounded-sm border border-grey-200 bg-white px-4 py-10 text-center shadow-flat">
        <div className="flex size-9 items-center justify-center rounded-s, bg-red-100 text-red-600">
          <Icon name="error" size={20} />
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="text-body font-medium text-black">Couldn&apos;t load recent batches</p>
          <p className="text-small text-grey-900">
            Something went wrong fetching your batch history.
          </p>
        </div>
        <Button
          hierarchy="secondary"
          size="sm"
          leftIcon={<Icon name="refresh" size={20} />}
          onClick={onRetry}
        >
          Retry
        </Button>
      </div>
    </section>
  );
}

function RecentBatchesEmpty({ canUpload }: { canUpload: boolean }) {
  return (
    <section className="flex w-full flex-col gap-2">
      <div className="flex flex-col items-center gap-2 rounded-sm border border-dashed border-grey-200 bg-grey-25 px-4 py-10 text-center">
        <div className="flex size-9 items-center justify-center rounded-sm bg-grey-100 text-grey-900">
          <Icon name="history" size={20} />
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="text-body font-medium text-black">No recent batches yet</p>
          <p className="text-small text-grey-900">
            {canUpload
              ? "Upload a file above to run your first batch screening."
              : "No batches have been screened yet."}
          </p>
        </div>
      </div>
    </section>
  );
}

type UploadCardProps = {
  onUpload: () => void;
  onFileDrop?: (file: File) => void;
  heading?: string;
  description?: ReactNode;
  infoText?: ReactNode;
  infoFontSize?: string;
  onDownloadTemplate?: () => void;
};

const DEFAULT_DESCRIPTION = (
  <>
    <p>Use the XLSX template for your input.</p>
    <p>See the Batch Screening Guide for format details and limits.</p>
  </>
);

const DEFAULT_INFO =
  "Valid records are screened against all enabled lists. Get the results by clicking the Download report button.";

function dashedBorderStyle(color: string): CSSProperties {
  const edge = (to: "right" | "bottom") =>
    `repeating-linear-gradient(to ${to}, ${color} 0, ${color} 4px, transparent 4px, transparent 10px)`;
  return {
    backgroundImage: [edge("right"), edge("right"), edge("bottom"), edge("bottom")].join(", "),
    backgroundPosition: "top left, bottom left, top left, top right",
    backgroundSize: "100% 1.5px, 100% 1.5px, 1.5px 100%, 1.5px 100%",
    backgroundRepeat: "repeat-x, repeat-x, repeat-y, repeat-y",
  };
}

export function UploadCard({
  onUpload,
  onFileDrop,
  heading = "Upload a new batch",
  description = DEFAULT_DESCRIPTION,
  infoText = DEFAULT_INFO,
  infoFontSize = "13px",
  onDownloadTemplate,
}: UploadCardProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    if (!onFileDrop) return;
    e.preventDefault();
    if (!e.dataTransfer.types.includes("Files")) return;
    dragCounter.current += 1;
    setIsDragging(true);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (!onFileDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    if (!onFileDrop) return;
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (!onFileDrop) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileDrop(file);
  }

  const buttonColumn = (
    <div className="flex w-full shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:min-w-[230px]">
      <Button
        hierarchy="primary"
        size="md"
        rightIcon={<Icon name="publish" size={20} />}
        onClick={onUpload}
      >
        Upload file
      </Button>
      <Button
        hierarchy="secondary"
        size="md"
        rightIcon={<Icon name="download" size={20} weight={300} />}
        onClick={onDownloadTemplate}
      >
        Download XLSX template
      </Button>
    </div>
  );

  const glyph = (
    <img src="/brand/File.svg" alt="" width={54} height={64} aria-hidden className="shrink-0" />
  );

  const textBlock = (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="text-body font-semibold text-black">{heading}</p>
      <div className="flex flex-col text-small text-grey-900">{description}</div>
    </div>
  );

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={
        !isDragging && onFileDrop
          ? dashedBorderStyle("color-mix(in srgb, var(--color-grey-500) 65%, white)")
          : undefined
      }
      className={[
        "flex w-full flex-col gap-2 rounded-md border p-4 transition-colors",
        onFileDrop ? "" : "shadow-flat",
        isDragging
          ? "border-solid border-green-500 bg-green-100"
          : onFileDrop
            ? "border-transparent bg-white"
            : "border-solid border-grey-200 bg-white",
      ].join(" ")}
    >
      <div className="flex w-full flex-col gap-4 p-4 sm:flex-row sm:items-center">
        {glyph}
        {textBlock}
        <div className="hidden flex-1 items-center justify-center self-stretch sm:flex">
          <div className="h-full w-px bg-grey-200" aria-hidden />
        </div>
        {buttonColumn}
      </div>

      <div className="flex w-full items-center justify-center rounded-sm bg-grey-50 p-4">
        <p className="flex-1 text-center text-grey-900" style={{ fontSize: infoFontSize }}>
          {infoText}
        </p>
      </div>

      {onFileDrop && (
        <p className="flex items-center justify-center gap-1 text-center text-tiny text-grey-600">
          <Icon name="file_upload" size={20} weight={300} />
          Click &ldquo;Upload file&rdquo; above, or drag and drop your file here
        </p>
      )}
    </div>
  );
}
